import { useEffect, useMemo, useState } from 'react'
import { api, type Evaluation, type Lookup } from '../lib/api'
import { money, currentPeriod } from '../lib/format'
import { Panel, Field, Alert, Loading, Empty, Tag } from '../components/ui'
import MonthRangePicker from '../components/MonthRangePicker'
import Combo from '../components/Combo'


const BLANK = {
  period: currentPeriod(),
  // 不在新增表單顯示，但要跟著送出，否則編輯既有資料時會把清單上的勾選清掉
  checked: false,
  // 預設值寫在這裡而不是只讓 <select> 顯示第一個選項，
  // 否則使用者沒動下拉時這個欄位不會被送出
  dispatch_type: '外包',
  client_id: 0,
  contract_code: '',
  case_link: '',
  owner_id: null as number | null,
  writer_id: null as number | null,
  ask_writer_on: '',
  due_on: '',
  eip_due_on: '',
  qty: 1,
  unit_price: 0,
  note: '',
}

export default function Evaluations() {
  const [from, setFrom] = useState(currentPeriod())
  const [to, setTo] = useState(currentPeriod())
  /** 跨月才需要顯示「月份」欄，單月時每列都一樣沒意義 */
  const multiMonth = from !== to
  const [rows, setRows] = useState<Evaluation[] | null>(null)
  const [lookup, setLookup] = useState<Lookup | null>(null)
  const [form, setForm] = useState({ ...BLANK })
  const [editing, setEditing] = useState<number | null>(null)
  const [error, setError] = useState('')
  const [msg, setMsg] = useState('')
  const [converting, setConverting] = useState(false)
  const [role, setRole] = useState('')
  /** 轉發包單是不可逆的動作（評估會被標記已發包），限管理員 */
  const isAdmin = role === 'admin'

  const load = () => {
    setRows(null)
    api.get<Evaluation[]>(`/evaluations?from=${from}&to=${to}`).then(setRows).catch((e) => setError(e.message))
  }

  useEffect(load, [from, to])
  useEffect(() => {
    api.get<Lookup>('/masters/lookup').then(setLookup).catch((e) => setError(e.message))
    api.get<{ user: { role: string } }>('/me').then((d) => setRole(d.user.role)).catch(() => {})
  }, [])

  // 選了寫手就自動帶名冊預設單價；內部發包的寫手同時把發包類型帶成「內包」（仍可手動改回）
  const pickWriter = (id: number) => {
    const w = lookup?.writers.find((x) => x.id === id)
    setForm((f) => ({
      ...f,
      writer_id: id || null,
      unit_price: w?.default_price ?? f.unit_price,
      dispatch_type: w?.kind === '內部發包' ? '內包' : f.dispatch_type,
    }))
  }

  const amount = useMemo(() => (form.qty || 0) * (form.unit_price || 0), [form.qty, form.unit_price])

  /** review=true：新增時直接送審，省掉「先新增再送出」的第二步 */
  const submit = async ({ review = false } = {}) => {
    setError('')
    try {
      if (!form.client_id) throw new Error('請選擇客戶')
      const payload = { ...form, period: editing ? form.period : to, amount }
      if (editing) await api.put(`/evaluations/${editing}`, payload)
      else await api.post('/evaluations', { ...payload, submit_for_review: review })
      setForm({ ...BLANK, period: to })
      setEditing(null)
      load()
    } catch (e: any) {
      setError(e.message)
    }
  }

  /** 存成草稿的評估，之後可以在列表直接送審 */
  const sendReview = async (r: Evaluation) => {
    if (!confirm(`確定送出「${r.client_name}」這筆評估審核？

送出後由管理員核准，核准才能轉發包單。`)) return
    setError('')
    try {
      await api.post(`/evaluations/${r.id}/submit`)
      load()
    } catch (e: any) {
      setError(e.message)
    }
  }

  const edit = (r: Evaluation) => {
    setEditing(r.id)
    setForm({
      period: r.period,
      checked: !!r.checked,
      dispatch_type: r.dispatch_type ?? '外包',
      client_id: r.client_id,
      contract_code: r.contract_code ?? '',
      case_link: r.case_link ?? '',
      owner_id: r.owner_id,
      writer_id: r.writer_id,
      ask_writer_on: r.ask_writer_on ?? '',
      due_on: r.due_on ?? '',
      eip_due_on: r.eip_due_on ?? '',
      qty: r.qty,
      unit_price: r.unit_price,
      note: r.note ?? '',
    })
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  /** 尚未轉發包單的才可以勾選；「檢查」勾選同時也是轉發包單的選取依據 */
  const selectable = (rows ?? []).filter((r) => !r.work_order_code)
  const selectedCount = selectable.filter((r) => r.checked).length
  const allSelected = selectable.length > 0 && selectedCount === selectable.length

  /** 切換單一列的「檢查」勾選，同時就是選取狀態 */
  const toggleCheck = async (r: Evaluation) => {
    setError('')
    const next = !r.checked
    // 先更新畫面，失敗再還原，避免每次點擊都要等伺服器
    setRows((list) => (list ?? []).map((x) => (x.id === r.id ? { ...x, checked: next ? 1 : 0 } : x)))
    try {
      await api.post(`/evaluations/${r.id}/check`, { checked: next })
    } catch (e: any) {
      setRows((list) => (list ?? []).map((x) => (x.id === r.id ? { ...x, checked: r.checked } : x)))
      setError(e.message)
    }
  }

  /** 全選／取消全選：等同於把可勾選的列全部打勾／取消 */
  const toggleSelectAll = async () => {
    const next = !allSelected
    const targets = selectable.filter((r) => !!r.checked !== next)
    if (targets.length === 0) return

    setRows((list) =>
      (list ?? []).map((x) => (targets.some((t) => t.id === x.id) ? { ...x, checked: next ? 1 : 0 } : x))
    )
    try {
      await Promise.all(targets.map((r) => api.post(`/evaluations/${r.id}/check`, { checked: next })))
    } catch (e: any) {
      setError(e.message)
      load()
    }
  }

  const convertSelected = async () => {
    const picked = selectable.filter((r) => r.checked)
    if (picked.length === 0) return

    const noWriter = picked.filter((r) => !r.writer_id)
    if (noWriter.length > 0) {
      setError(`以下項目尚未指定寫手，無法轉發包單：${noWriter.map((r) => r.client_name).join('、')}`)
      return
    }

    // 一筆評估一張發包單，不自動合併同一位寫手的多筆
    const detail = picked
      .map((r) => `　• ${r.writer_name ?? '—'}：${r.client_name}（${r.qty} 篇）`)
      .join('\n')
    const ok = confirm(
      `將把選取的 ${picked.length} 筆評估轉為 ${picked.length} 張發包單：\n\n${detail}\n\n` +
      `一筆評估各開一張，不會自動合併。\n` +
      `需要合併請到發包單列表勾選後按「合併發包單」。\n建立後可以再進去補發包日、交件日等細節。`
    )
    if (!ok) return

    setError(''); setMsg(''); setConverting(true)
    try {
      const res = await api.post<{ created: Array<{ code: string; writer_name: string; items: number }> }>(
        '/work-orders/bulk-from-evaluations',
        { evaluation_ids: picked.map((r) => r.id) }
      )
      setMsg(
        `已建立 ${res.created.length} 張發包單：` +
        res.created.map((c) => `${c.code}（${c.writer_name}，${c.items} 筆）`).join('、')
      )
      load()
    } catch (e: any) {
      setError(e.message)
    } finally {
      setConverting(false)
    }
  }

  const remove = async (r: Evaluation) => {
    const label = [r.client_name, r.contract_code, `${r.qty} 篇`].filter(Boolean).join(' ／ ')
    if (!confirm(`確定刪除這筆評估？\n\n${label}\n\n此動作無法復原。`)) return
    setError('')
    try {
      await api.del(`/evaluations/${r.id}`)
      // 正在編輯的就是被刪掉的那筆時，一併把表單清掉
      if (editing === r.id) {
        setEditing(null)
        setForm({ ...BLANK, period: to })
      }
      load()
    } catch (e: any) {
      setError(e.message)
    }
  }

  const totals = (rows ?? []).reduce(
    (acc, r) => ({ qty: acc.qty + r.qty, amount: acc.amount + r.amount }),
    { qty: 0, amount: 0 }
  )

  return (
    <>
      <div className="page-head">
        <h2>每月發包評估</h2>
        <span className="hint">月份</span>
        <MonthRangePicker from={from} to={to} onChange={(a, b) => { setFrom(a); setTo(b) }} />
        <div className="spacer" />
        <span className="hint">
          評估階段先登錄案件與預估金額；確認後按「轉發包單」進入下一關。
        </span>
      </div>

      <Alert kind="error">{error}</Alert>

      <Panel title={editing ? `編輯評估 #${editing}` : '新增評估'}>
        {/* 12 欄網格：日期與數字欄收窄，13 個欄位排成 3 列而不是 4 列 */}
        <div className="grid c12">
          <Field label="發包類型" span={2}>
            <select
              value={form.dispatch_type}
              onChange={(e) => setForm({ ...form, dispatch_type: e.target.value })}
            >
              <option value="外包">外包</option>
              <option value="內包">內包</option>
            </select>
          </Field>
          <Field label="客戶" span={3}>
            <Combo
              items={lookup?.clients ?? []}
              value={form.client_id}
              placeholder="輸入客戶名稱"
              allowEmpty={false}
              sub={(c) => c.short_name ?? ''}
              onChange={(c) => setForm((f) => ({ ...f, client_id: c ? c.id : 0 }))}
            />
          </Field>
          <Field label="合約編號" span={2}>
            <input
              value={form.contract_code}
              placeholder="例：F114631加簽1"
              onChange={(e) => setForm({ ...form, contract_code: e.target.value })}
            />
          </Field>
          <Field label="負責人" span={2}>
            <select
              value={form.owner_id ?? ''}
              onChange={(e) => setForm({ ...form, owner_id: Number(e.target.value) || null })}
            >
              <option value="">— 未指定 —</option>
              {lookup?.staff.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </Field>
          <Field label="發包寫手" span={3}>
            {/* 跟發包單一致的打字搜尋；評估階段可以還沒定寫手，所以允許留空 */}
            <Combo
              items={lookup?.writers ?? []}
              value={form.writer_id}
              placeholder="輸入寫手姓名（可留空）"
              search={(w) => `${w.name} ${w.name_en ?? ''}`}
              sub={(w) => `[${w.lang}] ${money(w.default_price)}`}
              onChange={(w) => pickWriter(w ? w.id : 0)}
            />
          </Field>

          <Field label="詢問＆大綱" span={2}>
            <input type="date" value={form.ask_writer_on} onChange={(e) => setForm({ ...form, ask_writer_on: e.target.value })} />
          </Field>
          <Field label="外包交稿日" span={2}>
            <input type="date" value={form.due_on} onChange={(e) => setForm({ ...form, due_on: e.target.value })} />
          </Field>
          <Field label="EIP 交付日" span={2}>
            <input type="date" value={form.eip_due_on} onChange={(e) => setForm({ ...form, eip_due_on: e.target.value })} />
          </Field>
          <Field label="篇數" span={1}>
            <input type="number" min={1} value={form.qty} onChange={(e) => setForm({ ...form, qty: Number(e.target.value) })} />
          </Field>
          <Field label="單價" span={2}>
            <input type="number" min={0} step={100} value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: Number(e.target.value) })} />
          </Field>
          <Field label="金額（自動）" span={3}>
            <input readOnly value={money(amount)} />
          </Field>

          <Field label="案件連結（Google 聊天室）" span={6}>
            <input value={form.case_link} onChange={(e) => setForm({ ...form, case_link: e.target.value })} placeholder="https://chat.google.com/room/…" />
          </Field>
          <Field label="備註" span={6}>
            <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </Field>
        </div>

        <div className="btn-row" style={{ marginTop: 14 }}>
          {editing ? (
            <button className="primary" onClick={() => submit()}>儲存變更</button>
          ) : (
            <>
              <button onClick={() => submit()}>儲存草稿</button>
              <button className="primary" onClick={() => submit({ review: true })}>送出審核</button>
            </>
          )}
          {editing && (
            <button onClick={() => { setEditing(null); setForm({ ...BLANK, period: to }) }}>取消編輯</button>
          )}
        </div>
      </Panel>

      {msg && <div className="alert ok">{msg}</div>}

      <Panel
        title={`${multiMonth ? `${from} ～ ${to}` : from} 評估清單`}
        actions={
          <div className="btn-row">
            <span className="hint" style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
              已勾選 {selectedCount} / {selectable.length} 筆可轉
            </span>
            <button onClick={toggleSelectAll} disabled={selectable.length === 0}>
              {allSelected ? '取消全選' : '全選'}
            </button>
            <button
              className="primary"
              disabled={selectedCount === 0 || converting || !isAdmin}
              title={isAdmin ? undefined : '只有管理員可以轉發包單'}
              onClick={convertSelected}
            >
              {converting ? '建立中…' : `轉發包單（${selectedCount}）`}
            </button>
          </div>
        }
      >
        {!rows ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>這個月還沒有評估紀錄</Empty>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th style={{ width: 44 }}>檢查</th>
                  {multiMonth && <th style={{ width: 74 }}>月份</th>}
                  <th style={{ width: 56 }}>類型</th>
                  <th>客戶</th>
                  <th>合約</th>
                  <th>負責人</th>
                  <th>寫手</th>
                  <th>詢問/大綱</th>
                  <th>外包交稿</th>
                  <th>EIP 交付</th>
                  <th className="num">篇數</th>
                  <th className="num">單價</th>
                  <th className="num">金額</th>
                  <th>狀態</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} style={r.checked ? { background: 'var(--accent-soft)' } : undefined}>
                    <td>
                      <input
                        type="checkbox"
                        style={{ width: 'auto' }}
                        checked={!!r.checked}
                        disabled={!!r.work_order_code}
                        title={
                          r.work_order_code
                            ? '已轉為發包單'
                            : '勾選＝已檢查，同時也會選取此筆以便批次轉發包單'
                        }
                        onChange={() => toggleCheck(r)}
                      />
                    </td>
                    {multiMonth && <td>{r.period}</td>}
                    <td>
                      {/* 內包才標色；外包是常態，跟著上色反而看不出重點 */}
                      {r.dispatch_type === '內包'
                        ? <span className="tag info">內包</span>
                        : <span style={{ color: 'var(--ink-soft)' }}>外包</span>}
                    </td>
                    <td>
                      {r.case_link ? (
                        <a href={r.case_link} target="_blank" rel="noreferrer">{r.client_name}</a>
                      ) : (
                        r.client_name
                      )}
                    </td>
                    <td>{r.contract_code}</td>
                    <td>{r.owner_name}</td>
                    <td>{r.writer_name}</td>
                    <td>{r.ask_writer_on}</td>
                    <td>{r.due_on}</td>
                    <td>{r.eip_due_on}</td>
                    <td className="num">{r.qty}</td>
                    <td className="num">{money(r.unit_price)}</td>
                    <td className="num">{money(r.amount)}</td>
                    <td>
                      {r.work_order_code ? (
                        <Tag value={`已發包 ${r.work_order_code}`} />
                      ) : (
                        <>
                          <Tag value={r.status} />
                          {/* 被退回的會回到「評估中」並帶著原因，滑過去看得到 */}
                          {r.status === '評估中' && r.review_note && (
                            <span
                              title={`${r.reviewed_at} 被 ${r.reviewed_by} 退回：${r.review_note}`}
                              style={{ color: 'var(--bad)', fontSize: 11, marginLeft: 4 }}
                            >
                              已退回
                            </span>
                          )}
                        </>
                      )}
                    </td>
                    <td>
                      <div className="btn-row">
                        {r.status === '評估中' && (
                          <button className="primary" onClick={() => sendReview(r)}>送出審核</button>
                        )}
                        <button onClick={() => edit(r)}>編輯</button>
                        <button
                          className="danger"
                          disabled={!!r.work_order_code}
                          title={
                            r.work_order_code
                              ? `已轉為發包單 ${r.work_order_code}，不可刪除`
                              : undefined
                          }
                          onClick={() => remove(r)}
                        >
                          刪除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  {/* 檢查＋（月份）＋類型＋客戶…EIP 交付，跨月時多一欄 */}
                  <td colSpan={multiMonth ? 10 : 9}>合計</td>
                  <td className="num">{totals.qty}</td>
                  <td></td>
                  <td className="num">{money(totals.amount)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Panel>
    </>
  )
}
